import { Component, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  CallMissedOutgoing,
  CallReceived,
  Checklist,
  EastOutlined,
  FeedOutlined,
  Inventory,
  OtherHouses,
  Paid,
  ReportOutlined,
  ShowChart,
  WestOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AppContext } from "App";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import { ChatAssistantButton } from "mui-toolkit/ChatAssistantButton";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import {
  FinancialStatementSchedule_Accounts_Liquid_View,
  SCHEDULE_FINANCIAL_STATEMENT_ACCOUNTTYPE_OTHER,
  SYSTEM_PROMPT_ACCOUNT_PARSER_LIQUID,
} from "./schedule_financial_statement/schedule_financial_statement_accounts_liquid";
import { FinancialStatementSchedule_Accounts_Payable_View } from "./schedule_financial_statement/schedule_financial_statement_accounts_payable";
import {
  FinancialStatementSchedule_Accounts_Receivable_View,
  SYSTEM_PROMPT_ACCOUNT_PARSER_TERMS,
} from "./schedule_financial_statement/schedule_financial_statement_accounts_receivable";
import { FinancialStatementSchedule_Confirmation } from "./schedule_financial_statement/schedule_financial_statement_confirmation";
import { FinancialStatementSchedule_Equities_View } from "./schedule_financial_statement/schedule_financial_statement_equities";
import {
  FinancialStatementSchedule_Inventories_View,
  SYSTEM_PROMPT_ACCOUNT_PARSER_OTHER,
} from "./schedule_financial_statement/schedule_financial_statement_inventories";
import { FinancialStatementSchedule_Notes_Receivable_View } from "./schedule_financial_statement/schedule_financial_statement_notes_receivable";
import { FinancialStatementSchedule_Prepaids_View } from "./schedule_financial_statement/schedule_financial_statement_prepaids";
import { FinancialStatementSchedule_RealEstate_View } from "./schedule_financial_statement/schedule_financial_statement_realestate";
import {
  schedule_financial_statement_accounts_liquid,
  schedule_financial_statement_accounts_payable,
  schedule_financial_statement_accounts_receivable,
  schedule_financial_statement_confirmation,
  schedule_financial_statement_equities,
  schedule_financial_statement_inventories,
  schedule_financial_statement_notes_receivable,
  schedule_financial_statement_prepaids,
  schedule_financial_statement_realestate,
  useFinancialStatementSchedule,
} from "./schedule_financial_statement/useFinancialStatementSchedule";

export const InterviewDialog = ({ year }) => {
  const { globalState } = useContext(AppContext);
  const { addAttachments } = useChatAssistantAttachments();

  // Use refs for all context functions to prevent infinite loops
  const addAttachmentsRef = useRef(addAttachments);

  const theme = useTheme();

  // Update refs when context functions change
  useEffect(() => {
    addAttachmentsRef.current = addAttachments;
  }, [addAttachments]);

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  const [activeStep, setActiveStep] = useState(0);

  // useFinancialStatementSchedule
  const { actions: financialStatementScheduleAccountsActions } = useFinancialStatementSchedule(
    {
      year: year,
      category: null,
      is_active: true,
    },
    []
  );

  // Use ref for actions to ensure complete stability
  const actionsRef = useRef(financialStatementScheduleAccountsActions);
  useEffect(() => {
    actionsRef.current = financialStatementScheduleAccountsActions;
  }, [financialStatementScheduleAccountsActions]);

  const [currentCategory, setCurrentCategory] = useState(null);

  useEffect(() => {
    if (steps[activeStep]?.key) {
      setCurrentCategory(steps[activeStep]?.key);
    }

    // Clear attachments when step changes
    addAttachmentsRef.current([]);
  }, [activeStep]);

  const _loadFilteredAccounts = useCallback(async () => {
    // Get current records from API, filtered by category
    let _filter = { year, is_active: true };
    if (currentCategory != schedule_financial_statement_confirmation) {
      _filter.category = currentCategory;
    } else {
      delete _filter.category;
    }
    const _currentAccounts = await actionsRef.current.loadFinancialStatementScheduleAccounts(_filter);
    setCurrentAccounts(_currentAccounts);
  }, [year, currentCategory]);

  // Use ref to avoid circular dependencies with _loadFilteredAccounts
  const loadFilteredAccountsRef = useRef(_loadFilteredAccounts);
  useEffect(() => {
    loadFilteredAccountsRef.current = _loadFilteredAccounts;
  }, [_loadFilteredAccounts]);

  const [currentAccounts, setCurrentAccounts] = useState([]);
  useEffect(() => {
    if (currentCategory) {
      _loadFilteredAccounts();
    }
  }, [currentCategory, year, _loadFilteredAccounts]);

  // Update attachments whenever currentAccounts changes
  useEffect(() => {
    if (currentAccounts && currentAccounts.length > 0) {
      addAttachmentsRef.current([{ name: "Accounts", content: currentAccounts }]);
    } else {
      addAttachmentsRef.current([]);
    }
  }, [currentAccounts]);

  // --- --- ---

  const handleOnChangeObject = useCallback(
    async (account) => {
      account.category = steps[activeStep]?.key;
      account.year = year;
      if (!account.financial_institution_name) account.financial_institution_name = "Institution Name";
      if (!account.account_name) account.account_name = "New Account";
      if (!account.account_type) account.account_type = SCHEDULE_FINANCIAL_STATEMENT_ACCOUNTTYPE_OTHER;

      await actionsRef.current.updateFinancialStatementScheduleAccounts(account);
      // Use ref to avoid circular dependency
      loadFilteredAccountsRef.current();
    },
    [activeStep, year]
  );

  // Use ref for handleOnChangeObject to prevent infinite loops
  const handleOnChangeObjectRef = useRef(handleOnChangeObject);
  useEffect(() => {
    handleOnChangeObjectRef.current = handleOnChangeObject;
  }, [handleOnChangeObject]);

  const handleOnChangeRecords = useCallback(
    async (records) => {
      if (records && records.length) {
        const account = records[0];
        await handleOnChangeObjectRef.current(account);
      }
    },
    [] // No dependencies since we use ref
  );

  useEffect(() => {
    return () => {
      // Clear attachments when component unmounts
      addAttachmentsRef.current([]);
    };
  }, []); // Empty dependency array since handleOnChangeRecords is now stable

  // --- --- ---

  const steps = useMemo(
    () => [
      {
        category: "Financial Schedule",
        description: "",
        label: "Cash, Savings, CDs",
        icon: FeedOutlined,
        key: schedule_financial_statement_accounts_liquid,
        systemPrompt: SYSTEM_PROMPT_ACCOUNT_PARSER_LIQUID,
        promptName: "form_financialinterview_liquid",
        isValid: () => {
          return true;
        },
      },
      {
        category: "Financial Schedule",
        description: "",
        label: "Accounts Receivable",
        icon: CallReceived,
        key: schedule_financial_statement_accounts_receivable,
        systemPrompt: SYSTEM_PROMPT_ACCOUNT_PARSER_TERMS,
        promptName: "form_financialinterview_term",
        isValid: () => {
          return true;
        },
      },
      {
        category: "Financial Schedule",
        description: "",
        label: "Accounts Payable",
        icon: CallMissedOutgoing,
        key: schedule_financial_statement_accounts_payable,
        systemPrompt: SYSTEM_PROMPT_ACCOUNT_PARSER_TERMS,
        promptName: "form_financialinterview_term",
        isValid: () => {
          return true;
        },
      },
      {
        category: "Financial Schedule",
        description: "",
        label: "Inventories",
        icon: Inventory,
        key: schedule_financial_statement_inventories,
        systemPrompt: SYSTEM_PROMPT_ACCOUNT_PARSER_OTHER,
        promptName: "form_financialinterview_other",
        isValid: () => {
          return true;
        },
      },
      {
        category: "Financial Schedule",
        description: "",
        label: "Prepaids",
        icon: Paid,
        key: schedule_financial_statement_prepaids,
        systemPrompt: SYSTEM_PROMPT_ACCOUNT_PARSER_OTHER,
        promptName: "form_financialinterview_other",
        isValid: () => {
          return true;
        },
      },
      {
        category: "Financial Schedule",
        description: "",
        label: "Notes Receivable",
        icon: CallReceived,
        key: schedule_financial_statement_notes_receivable,
        systemPrompt: SYSTEM_PROMPT_ACCOUNT_PARSER_OTHER,
        promptName: "form_financialinterview_other",
        isValid: () => {
          return true;
        },
      },
      {
        category: "Financial Schedule",
        description: "",
        label: "Equities",
        icon: ShowChart,
        key: schedule_financial_statement_equities,
        systemPrompt: SYSTEM_PROMPT_ACCOUNT_PARSER_OTHER,
        promptName: "form_financialinterview_other",
        isValid: () => {
          return true;
        },
      },
      {
        category: "Financial Schedule",
        description: "",
        label: "Real Estate",
        icon: OtherHouses,
        key: schedule_financial_statement_realestate,
        systemPrompt: SYSTEM_PROMPT_ACCOUNT_PARSER_OTHER,
        promptName: "form_financialinterview_other",
        isValid: () => {
          return true;
        },
      },
      {
        category: "Financial Schedule",
        description: "",
        label: "Confirmation",
        icon: ReportOutlined,
        key: schedule_financial_statement_confirmation,
        systemPrompt: null,
        promptName: null,
        isValid: () => {
          return false;
        },
      },
    ],
    []
  ); // Empty dependency array since this is static data

  class HorizontalStepContent extends Component {
    render() {
      const thisStep = this?.props?.step;

      return <>{steps[activeStep]?.key == thisStep?.key && <Typography>{thisStep?.description}</Typography>}</>;
    }
  }

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Create step icon components that filter out invalid props
  const createStepIconComponent = (IconComponent) => {
    return (props) => {
      const { error, completed, active, ...svgProps } = props;
      return (
        <IconComponent
          {...svgProps}
          sx={{
            color: active ? theme.palette.primary.main : theme.palette.text.secondary,
            ...svgProps.sx,
          }}
        />
      );
    };
  };

  Array.prototype.indexOfId = function (id) {
    for (var i = 0; i < this.length; i++) if (this[i].id === id) return i;
    return -1;
  };

  // --- --- ---

  return (
    <>
      <DialogTitle>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{
            minHeight: { xs: "150px", md: "85px" }, // Reserve space for the chat helper
            alignItems: { xs: "stretch", md: "flex-start" },
          }}
        >
          <Stack>
            <Box>{steps[activeStep]?.category}</Box>
            <Box>
              <strong>{steps[activeStep]?.label}</strong>
            </Box>
          </Stack>

          {Boolean(steps[activeStep].systemPrompt) && (
            <Stack sx={{ flexGrow: 1 }}>
              <ChatAssistantButton
                promptName={steps[activeStep]?.promptName}
                systemPrompt={steps[activeStep]?.systemPrompt}
                onChangeRecords={handleOnChangeRecords}
              />
            </Stack>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent className="interview-dialog-content">
        {steps[activeStep].key == schedule_financial_statement_accounts_liquid && (
          <>
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-around"
              justifyItems="stretch"
              sx={{ mt: isSmall ? "4px" : "50px" }}
            >
              <FinancialStatementSchedule_Accounts_Liquid_View
                year={year}
                accounts={currentAccounts}
                onChange={handleOnChangeObject}
                is_lender={globalState?.is_lender}
              />
            </Stack>
          </>
        )}

        {steps[activeStep].key == schedule_financial_statement_accounts_receivable && (
          <>
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-around"
              justifyItems="stretch"
              sx={{ mt: isSmall ? "4px" : "50px" }}
            >
              <FinancialStatementSchedule_Accounts_Receivable_View
                year={year}
                accounts={currentAccounts}
                onChange={handleOnChangeObject}
                is_lender={globalState?.is_lender}
              />
            </Stack>
          </>
        )}

        {steps[activeStep].key == schedule_financial_statement_accounts_payable && (
          <>
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-around"
              justifyItems="stretch"
              sx={{ mt: isSmall ? "4px" : "50px" }}
            >
              <FinancialStatementSchedule_Accounts_Payable_View
                year={year}
                accounts={currentAccounts}
                onChange={handleOnChangeObject}
                is_lender={globalState?.is_lender}
              />
            </Stack>
          </>
        )}

        {steps[activeStep].key == schedule_financial_statement_inventories && (
          <>
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-around"
              justifyItems="stretch"
              sx={{ mt: isSmall ? "4px" : "50px" }}
            >
              <FinancialStatementSchedule_Inventories_View
                year={year}
                accounts={currentAccounts}
                onChange={handleOnChangeObject}
                is_lender={globalState?.is_lender}
              />
            </Stack>
          </>
        )}

        {steps[activeStep].key == schedule_financial_statement_prepaids && (
          <>
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-around"
              justifyItems="stretch"
              sx={{ mt: isSmall ? "4px" : "50px" }}
            >
              <FinancialStatementSchedule_Prepaids_View
                year={year}
                accounts={currentAccounts}
                onChange={handleOnChangeObject}
                is_lender={globalState?.is_lender}
              />
            </Stack>
          </>
        )}
        {steps[activeStep].key == schedule_financial_statement_notes_receivable && (
          <>
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-around"
              justifyItems="stretch"
              sx={{ mt: isSmall ? "4px" : "50px" }}
            >
              <FinancialStatementSchedule_Notes_Receivable_View
                year={year}
                accounts={currentAccounts}
                onChange={handleOnChangeObject}
                is_lender={globalState?.is_lender}
              />
            </Stack>
          </>
        )}
        {steps[activeStep].key == schedule_financial_statement_equities && (
          <>
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-around"
              justifyItems="stretch"
              sx={{ mt: isSmall ? "4px" : "50px" }}
            >
              <FinancialStatementSchedule_Equities_View
                year={year}
                accounts={currentAccounts}
                onChange={handleOnChangeObject}
                is_lender={globalState?.is_lender}
              />
            </Stack>
          </>
        )}
        {steps[activeStep].key == schedule_financial_statement_realestate && (
          <>
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-around"
              justifyItems="stretch"
              sx={{ mt: isSmall ? "4px" : "50px" }}
            >
              <FinancialStatementSchedule_RealEstate_View
                year={year}
                accounts={currentAccounts}
                onChange={handleOnChangeObject}
                is_lender={globalState?.is_lender}
              />
            </Stack>
          </>
        )}

        {steps[activeStep].key == schedule_financial_statement_confirmation && (
          <>
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-around"
              justifyItems="stretch"
              sx={{ mt: isSmall ? "4px" : "50px" }}
            >
              <FinancialStatementSchedule_Confirmation
                //year={year}
                accounts={currentAccounts}
                //onChange={handleOnChangeObject}
                //is_lender={globalState?.is_lender}
              />
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: 0 }}>
        {!isSmall && (
          <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
            <>
              <Box
                sx={{
                  flexGrow: 1,
                  overflowX: "auto",
                  overflowY: "hidden",
                  borderRight: `1px solid ${theme.palette.primary.light}`,
                }}
              >
                <Stepper
                  activeStep={activeStep}
                  orientation="horizontal"
                  alternativeLabel
                  sx={{
                    minWidth: "max-content",
                    paddingTop: 2,
                    paddingBottom: 2,
                  }}
                >
                  {steps.map((step, index) => (
                    <Step
                      key={index}
                      onClick={() => {
                        setActiveStep(index);
                      }}
                    >
                      <StepLabel optional={null} StepIconComponent={createStepIconComponent(step?.icon || Checklist)}>
                        {step.label || ""}
                      </StepLabel>
                      <HorizontalStepContent step={step}>
                        <Typography>{step.description}</Typography>
                      </HorizontalStepContent>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems={"center"}
                sx={{ flexShrink: 0, paddingLeft: 2, paddingRight: 2 }}
              >
                <Button variant="contained" color="primary" disabled={activeStep === 0} onClick={handleBack}>
                  <WestOutlined sx={{ mr: 1 }} />
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={activeStep >= steps.length - 1}
                  onClick={handleNext}
                >
                  <EastOutlined sx={{ mr: 1 }} />
                  Next
                </Button>
              </Stack>
            </>
          </Box>
        )}

        {isSmall && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
                p: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                width: "100%",
              }}
            >
              {steps.map((step, index) => {
                const IconComponent = step?.icon || Checklist;
                const isActive = activeStep === index;
                return (
                  <Button
                    key={index}
                    variant="text"
                    color="inherit"
                    onClick={() => setActiveStep(index)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                      py: 1,
                      px: 0.5,
                      minHeight: "60px",
                      fontSize: "10px",
                      textTransform: "none",
                      lineHeight: 1.2,
                      color: isActive ? theme.palette.primary.main : "inherit",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <IconComponent
                      sx={{
                        fontSize: "20px",
                        color: isActive ? theme.palette.primary.main : "inherit",
                      }}
                    />
                    <Box component="span" sx={{ textAlign: "center", fontSize: "10px" }}>
                      {step.label}
                    </Box>
                  </Button>
                );
              })}
            </Box>
            <WorkspaceActionBar
              content={
                <>
                  <Button variant="contained" color="primary" disabled={activeStep === 0} onClick={handleBack}>
                    <WestOutlined sx={{ mr: 1 }} />
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={activeStep >= steps.length - 1}
                    onClick={handleNext}
                  >
                    <EastOutlined sx={{ mr: 1 }} />
                    Next
                  </Button>
                </>
              }
            />
          </>
        )}
      </DialogActions>
    </>
  );
};
